import { useCallback, useMemo, useState } from 'react';

import { DateTime } from 'luxon';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { useTripsBulkPriceCreateMutation } from 'src/common/external/bambi-api/bambiApi';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { tripSlice } from 'src/features/trip/trip.slice';

const TRIPS_PAST_DATE_RANGE = 30;
const TRIPS_FUTURE_DATE_RANGE = 30;

export function useSelectedTripsStepViewModel(
  next: () => void,
  setError: (error: string | null) => void
) {
  const dispatch = useAppDispatch();
  const tripsToReprice = useAppSelector(
    (state) => state.trip.bulkPriceModal.tripsToReprice
  );
  const [tripsBulkPriceCreateMutation] = useTripsBulkPriceCreateMutation();
  const [loading, setLoading] = useState(false);

  const tripsOutsideDateRange = useMemo(() => {
    return tripsToReprice.filter((trip) => {
      const pickupAt = DateTime.fromISO(trip.scheduled_pickup_at);
      const now = DateTime.now();
      const pastWindow = now
        .minus({ days: TRIPS_PAST_DATE_RANGE })
        .startOf('day');
      const futureWindow = now
        .plus({ days: TRIPS_FUTURE_DATE_RANGE })
        .endOf('day');
      return pickupAt < pastWindow || pickupAt > futureWindow;
    });
  }, [tripsToReprice]);

  const tripsToPrice = useMemo(() => {
    return tripsToReprice.filter((trip) => {
      return !tripsOutsideDateRange.includes(trip);
    });
  }, [tripsToReprice, tripsOutsideDateRange]);

  const tableClass =
    tripsOutsideDateRange.length > 0 && tripsToPrice.length > 0
      ? 'max-h-[25vh]'
      : 'max-h-[50vh]';

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const repriceResponse = await tripsBulkPriceCreateMutation({
        bulkTripPriceChange: {
          trip_ids: tripsToPrice.map((trip) => trip.id),
        },
      }).unwrap();

      dispatch(tripSlice.actions.onBulkPriceTripsDone(repriceResponse));
      next();
    } catch (error) {
      setError(formatServerError(error));
    } finally {
      setLoading(false);
    }
  }, [setError, tripsBulkPriceCreateMutation, tripsToPrice, dispatch, next]);

  return {
    handleSubmit,
    loading,
    tableClass,
    tripsOutsideDateRange,
    tripsToPrice,
  };
}
